import { Router } from 'express';
import { CLIService } from '../services/cli-service.js';
import { ApiError } from '../types.js';

export const cliRouter = Router();
const cliService = new CLIService();

// 执行 CLI 命令
cliRouter.post('/execute', async (req, res, next) => {
  try {
    const { command, subcommand, args, options, workspaceId } = req.body;
    
    if (!command) {
      throw new ApiError(400, 'Command is required');
    }

    // 验证命令是否允许
    const isValid = await cliService.validateCommand(command, subcommand || '');
    if (!isValid) {
      throw new ApiError(403, 'Command not allowed');
    }

    // 执行命令
    const result = await cliService.execute({
      command,
      subcommand,
      args,
      options,
      workspaceId
    });

    // 根据执行结果返回相应的状态码
    if (!result.success) {
      if (result.exitCode === 408) {
        throw new ApiError(408, result.error || 'Command timeout');
      } else if (result.exitCode === 503) {
        throw new ApiError(503, result.error || 'CLI unavailable');
      } else {
        throw new ApiError(500, result.error || 'Command failed');
      }
    }

    res.success(result);
  } catch (error) {
    next(error);
  }
});

// 验证命令
cliRouter.post('/validate', async (req, res, next) => {
  try {
    const { command, subcommand } = req.body;
    
    if (!command) {
      throw new ApiError(400, 'Command is required');
    }

    const valid = await cliService.validateCommand(command, subcommand || '');
    
    res.success({ valid });
  } catch (error) {
    next(error);
  }
});

// 获取 CLI 状态
cliRouter.get('/status', async (req, res, next) => {
  try {
    const status = await cliService.getStatus();
    res.success(status);
  } catch (error) {
    next(error);
  }
});

// 快捷命令：创建任务
cliRouter.post('/task/create', async (req, res, next) => {
  try {
    const { content, priority, tags } = req.body;
    
    if (!content) {
      throw new ApiError(400, 'Task content is required');
    }

    const options: Record<string, any> = {};
    if (priority) options.priority = priority;
    if (tags && tags.length > 0) options.tags = tags.join(',');

    const result = await cliService.taskCommand('add', [content], options);
    
    if (!result.success) {
      throw new ApiError(500, result.error || 'Failed to create task');
    }

    res.success(result);
  } catch (error) {
    next(error);
  }
});

// 快捷命令：更新任务
cliRouter.put('/task/:id', async (req, res, next) => {
  try {
    const { id } = req.params;
    const { content, status, priority, tags } = req.body;
    
    const args = [id];
    const options: Record<string, any> = {};
    
    if (content) args.push(content);
    if (status) options.status = status;
    if (priority) options.priority = priority;
    if (tags) options.tags = Array.isArray(tags) ? tags.join(',') : tags;

    const result = await cliService.taskCommand('update', args, options);
    
    if (!result.success) {
      throw new ApiError(500, result.error || 'Failed to update task');
    }

    res.success(result);
  } catch (error) {
    next(error);
  }
});

// 快捷命令：删除任务
cliRouter.delete('/task/:id', async (req, res, next) => {
  try {
    const { id } = req.params;
    
    const result = await cliService.taskCommand('delete', [id]);
    
    if (!result.success) {
      throw new ApiError(500, result.error || 'Failed to delete task');
    }

    res.success(result);
  } catch (error) {
    next(error);
  }
});

// 快捷命令：启动任务
cliRouter.post('/task/:id/start', async (req, res, next) => {
  try {
    const { id } = req.params;
    
    const result = await cliService.taskCommand('start', [id]);
    
    if (!result.success) {
      throw new ApiError(500, result.error || 'Failed to start task');
    }

    res.success(result);
  } catch (error) {
    next(error);
  }
});

// 快捷命令：完成任务
cliRouter.post('/task/:id/complete', async (req, res, next) => {
  try {
    const { id } = req.params;
    const { notes } = req.body;
    
    const options: Record<string, any> = {};
    if (notes) options.notes = notes;
    
    const result = await cliService.taskCommand('complete', [id], options);
    
    if (!result.success) {
      throw new ApiError(500, result.error || 'Failed to complete task');
    }

    res.success(result);
  } catch (error) {
    next(error);
  }
});